import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data";
import Logo from "./Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const to = (hash) => (isHome ? hash : `/${hash}`);
  const homeHref = isHome ? "#top" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-md border-b border-[#333]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <a
          href={homeHref}
          className="text-white hover:opacity-80 transition-opacity"
          data-testid="logo"
        >
          <Logo markSize={30} textClassName="text-2xl" />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={to(l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#FF4D00] transition-colors"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={to("#contact")}
            className="font-mono text-xs uppercase tracking-[0.2em] border border-[#FF4D00] text-[#FF4D00] px-5 py-2.5 hover:bg-[#FF4D00] hover:text-black transition-colors"
            data-testid="nav-get-quote"
          >
            Get a Quote
          </a>
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu size={26} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0A0A0A] flex flex-col p-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center h-20">
              <span className="font-display font-black text-2xl tracking-tighter uppercase">
                <Logo markSize={28} textClassName="text-2xl" />
              </span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-8 mt-16">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={to(l.href)}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="font-display text-4xl font-bold uppercase tracking-tight hover:text-[#FF4D00]"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
